
import { motion } from 'framer-motion';
import { useState } from 'react';

export default function MissionDashboard() {
  const [activeTab, setActiveTab] = useState('active');

  const missions = [
    {
      category: 'Learning',
      title: 'Financial Literacy Basics',
      description: 'Master the fundamentals of personal finance and money management',
      progress: 65,
      hmu: 100,
      color: 'honey',
    },
    {
      category: 'Shopping',
      title: 'Amazon Product Review',
      description: 'Write detailed reviews for 3 products you recently purchased',
      progress: 33,
      hmu: 50,
      color: 'blue',
    },
    {
      category: 'Learning',
      title: 'Advanced Entrepreneur Module',
      description: 'Complete advanced business strategy lessons and case studies',
      progress: 20,
      hmu: 300,
      color: 'honey',
    },
    {
      category: 'Shopping',
      title: 'TikTok Shop Engagement',
      description: 'Browse and wishlist 5 trending products on TikTok Shop',
      progress: 80,
      hmu: 25,
      color: 'blue',
    },
    {
      category: 'Learning',
      title: 'Digital Marketing Fundamentals',
      description: 'Learn social media marketing and content creation strategies',
      progress: 45,
      hmu: 150,
      color: 'honey',
    },
    {
      category: 'Shopping',
      title: 'Temu Shopping Spree',
      description: 'Complete a purchase and share your experience with the community',
      progress: 10,
      hmu: 200,
      color: 'blue',
    },
  ];

  return (
    <section id="earn" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-[40%_60%] gap-8">
          {/* Left Panel - User Stats */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-charcoal-800 to-charcoal-900 rounded-3xl p-8 shadow-2xl"
          >
            {/* User Profile Card */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-6 border border-white/20">
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-honey-400 to-honey-600 flex items-center justify-center text-3xl font-bold text-white mb-4 ring-4 ring-honey-500/30">
                  JD
                </div>
                <h3 className="text-xl font-bold text-white mb-2">John Doe</h3>
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-coin-fill text-2xl text-honey-500"></i>
                  <span className="text-3xl font-bold text-honey-500">2,450</span>
                  <span className="text-white/70 text-sm">HMUs</span>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full">
                  <div className="flex justify-between text-xs text-white/70 mb-2">
                    <span>Achiever Tier</span>
                    <span>75%</span>
                  </div>
                  <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: '75%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.5 }}
                      className="h-full bg-gradient-to-r from-honey-400 to-honey-600"
                    ></motion.div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-3 mb-6">
              {[
                { label: 'Active Missions', icon: 'ri-compass-line', active: true },
                { label: 'Completed', icon: 'ri-checkbox-circle-line', active: false },
                { label: 'Leaderboard', icon: 'ri-trophy-line', active: false },
              ].map((action, index) => (
                <button
                  key={index}
                  className={`w-full px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-3 whitespace-nowrap ${
                    action.active
                      ? 'bg-honey-500 text-white shadow-lg'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  <i className={`${action.icon} text-xl`}></i>
                  {action.label}
                </button>
              ))}
            </div>

            {/* Streak Counter */}
            <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center">
                  <i className="ri-fire-fill text-5xl text-white"></i>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">7 Days</div>
                  <div className="text-white/90 text-sm">Current Streak 🔥</div>
                </div>
              </div>
              <div className="mt-4 text-white/80 text-xs">
                Keep it up! 3 more days for a 50 HMU bonus
              </div>
            </div>
          </motion.div>

          {/* Right Panel - Mission Cards */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="mb-8">
              <h2 className="text-4xl font-bold text-charcoal-800 mb-2">Your Missions</h2>
              <p className="text-gray-600">Complete missions to earn HMUs and level up</p>
            </div>

            {/* Mission Cards Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {missions.map((mission, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -4, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
                  className="bg-white rounded-2xl p-6 shadow-md cursor-pointer"
                >
                  {/* Category Badge */}
                  <div className="mb-4">
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        mission.color === 'honey'
                          ? 'bg-honey-100 text-honey-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {mission.category}
                    </span>
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-lg font-bold text-charcoal-800 mb-2 line-clamp-2">
                    {mission.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {mission.description}
                  </p>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Progress</span>
                      <span>{mission.progress}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          mission.color === 'honey' ? 'bg-honey-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${mission.progress}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Bottom Row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="ri-coin-fill text-xl text-honey-500"></i>
                      <span className="text-lg font-bold text-honey-600">+{mission.hmu}</span>
                      <span className="text-xs text-gray-500">HMU</span>
                    </div>
                    <button className="px-4 py-2 bg-charcoal-800 text-white rounded-lg text-sm font-semibold hover:bg-charcoal-700 transition-all whitespace-nowrap">
                      Continue
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
